import { createContext, useCallback, useContext, useMemo } from "react"

import { usePdfBuilderStore } from "@/stores/pdfBuilder"
import { createAccountStore } from "@/stores/account"
import { endOfDay, parseISO, startOfDay } from "date-fns"

import {
  useActivityDetailQuery,
  useActivityParticipatedPlayerListQuery,
  useActivityOverviewListQuery,
} from "@/hooks/api/useActivityQuery"
import {
  useTrendPlayerActivitySummaryQuery,
  useTrendPlayerSummaryQuery,
} from "@/hooks/api/useReportQuery"
import { ReportStatus } from "@/types/report"
import type { TrendActivityRow, TrendPlayerRow } from "@/app/report/common/sections/trendGraph"
import { useBuilderReportTemplate } from "@/app/export/pdf/hooks/useBuilderReportTemplate"
import { useMetricList } from "@/hooks/api/useMetricQuery"
import { useUnitQuery } from "@/hooks/api/useSettingQuery"
import { usePlayerSummaryByMode } from "@/app/report/hooks/usePlayerSummaryByMode"
import { ACTIVITY_SESSION_TYPE } from "@/app/report/common/constants"
import type { ActivityDetail, ActivityParticipatedPlayerType } from "@/types/activity"
import type { PlayerSessionSummary, ReportTemplate } from "@/types/report"
import type { MetricMeta } from "@/types/metric"
import { Unit } from "@/types/setting"
import type { Team } from "@/types/team"

interface PdfDataContextType {
  activityDetail: ActivityDetail | null
  /** 현재 모드(액티비티/세션)에 맞는 선수 요약. 리포트 화면과 같은 API·같은 훅에서 온다 (#75) */
  playerSummary: PlayerSessionSummary | null
  /** Report Filters 의 액티비티/세션 모드 — 리포트 화면의 dailyReportType 과 같은 의미 */
  reportMode: ACTIVITY_SESSION_TYPE
  /** 섹션 설정(지표·제목·계산 방식)의 출처인 리포트 템플릿. Report Filters 에서 고르고, 없으면 최근 사용 템플릿 */
  reportTemplate: ReportTemplate | null
  /** 실제 적용 중인 리포트 템플릿 ID (필터 미선택이면 최근 사용 템플릿) */
  reportTemplateId: number | null
  /** Report Filters 에서 선택된 선수 (액티비티 참여 선수 ∩ filterPlayerIds). 모든 컴포넌트의 선수 상한. */
  filterPlayers: ActivityParticipatedPlayerType[]
  /**
   * 컴포넌트(Data Source)에 저장된 선수 ID 를 Report Filters 범위 안으로 좁힌다.
   * - undefined(한 번도 지정 안 함): 필터 선수 전체
   * - [](사용자가 전부 해제): 빈 배열 — 상위 필터와 같게 "선택 0명 = 데이터 없음"
   * - 필터와 겹치는 선수가 하나도 없음(다른 팀·액티비티용 템플릿): 필터 선수 전체
   */
  resolveItemPlayerIds: (itemPlayerIds?: number[]) => number[]
  /** 액티비티가 선택됐는가 — 빈 상태 문구를 "액티비티를 고르세요" / "데이터 없음" 중 어느 쪽으로 낼지 가른다. */
  hasActivitySelection: boolean
  /** 기간의 액티비티 목록·대상 액티비티가 아직 정해지는 중인가 — 컴포넌트를 통째로 로딩으로 덮는다 */
  isFilterResolving: boolean
  /** 액티비티·(세션 모드면 세션)·선수가 모두 선택돼 실제 조회가 일어나는 상태인가. */
  hasFilterSelection: boolean
  /** 만들고 있는 리포트 — 액티비티(daily) / 트렌드(trend) (2026-10-02 요청) */
  reportKind: "daily" | "trend"
  /** 트렌드: 기간 안의 선수별 집계 (Chart by Player·Table 의 출처) */
  trendPlayers: TrendPlayerRow[]
  /** 트렌드: 기간 안의 액티비티별 집계 (Chart by Activity 의 출처) */
  trendActivities: TrendActivityRow[]
  /** 트렌드 집계 대상 액티비티 — 기간 안의 분석 완료 액티비티 (Table 도 이 범위로 집계한다) */
  trendActivityIds: number[]
  /** 트렌드에서 고를 수 있는 선수 전체 — 기간 안 액티비티들의 참가 선수 (Report Filters 의 선수 목록) */
  trendPlayerCandidates: ActivityParticipatedPlayerType[]
  metricList: MetricMeta[]
  metricMap: Map<string, MetricMeta>
  unitList: Unit[]
  isLoading: boolean
  teamInfo: Team | undefined
}

const PdfDataContext = createContext<PdfDataContextType>({
  activityDetail: null,
  playerSummary: null,
  reportMode: ACTIVITY_SESSION_TYPE.SESSION,
  reportTemplate: null,
  reportTemplateId: null,
  filterPlayers: [],
  resolveItemPlayerIds: () => [],
  hasActivitySelection: false,
  isFilterResolving: false,
  hasFilterSelection: false,
  metricList: [],
  metricMap: new Map(),
  unitList: [],
  isLoading: false,
  teamInfo: undefined,
  reportKind: "daily",
  trendPlayers: [],
  trendActivities: [],
  trendActivityIds: [],
  trendPlayerCandidates: [],
})

export function PdfDataProvider({ children }: { children: React.ReactNode }) {
  const filterActivityId = usePdfBuilderStore((s) => s.filterActivityId)
  const filterSessionIds = usePdfBuilderStore((s) => s.filterSessionIds)
  const filterPlayerIds = usePdfBuilderStore((s) => s.filterPlayerIds)
  const reportMode = usePdfBuilderStore((s) => s.filterReportMode)
  const reportKind = usePdfBuilderStore((s) => s.filterReportKind)
  const period = usePdfBuilderStore((s) => s.filterPeriod)
  const isTrend = reportKind === "trend"
  const teamInfo = createAccountStore((state) => state.teamProfileInfo)

  const { data: activityDetail, isLoading: isActivityLoading } = useActivityDetailQuery(
    filterActivityId ?? 0,
    !!filterActivityId,
  )

  // 리포트 템플릿 — 편집 페이지(초기 배치 시드)와 같은 훅으로 정한다
  const {
    reportTemplateId,
    reportTemplate,
    isLoading: isTemplateLoading,
  } = useBuilderReportTemplate()

  // Report Filters 의 모드·세션·선수 선택이 데이터 범위다. 선수를 모두 해제하면(0명) 조회하지 않는다 —
  // players 를 비워 보내면 API 가 "전체 선수" 로 해석해 필터가 무시되기 때문.
  // 액티비티 모드는 액티비티 요약, 세션 모드는 선택 세션 요약 — 리포트 화면과 같은 훅 (#75)
  const { playerSummary, isLoading: isSummaryLoading } = usePlayerSummaryByMode({
    mode: reportMode,
    activityId: filterActivityId,
    sessionIds: filterSessionIds,
    playerIds: filterPlayerIds,
  })

  // ── 트렌드: 기간 안의 분석 완료 액티비티 전체가 범위. 선수는 그 액티비티들의 참가 선수에서 고른다 (2026-10-02 요청).
  // 기간으로 조회하므로 기간을 바꾸면 쿼리 키가 바뀌어 목록부터 집계까지 다시 불러온다
  const { data: periodActivities, isLoading: isPeriodActivitiesLoading } =
    useActivityOverviewListQuery({
      start: startOfDay(parseISO(period.start)).valueOf(),
      end: endOfDay(parseISO(period.end)).valueOf(),
    })
  const analyzedActivityIds = useMemo(
    () =>
      (periodActivities ?? [])
        .filter(
          (activity) => activity.status === ReportStatus.Analyzed || !!activity.analyzeFinished,
        )
        .map((activity) => activity.idfActivity)
        .filter((id): id is number => typeof id === "number"),
    [periodActivities],
  )
  const trendActivityIds = useMemo(
    () => (isTrend ? analyzedActivityIds : []),
    [isTrend, analyzedActivityIds],
  )

  /**
   * 기간의 액티비티 목록을 받고, 거기서 이 리포트가 볼 액티비티가 정해지기 전까지 — 처음 들어왔을 때가 그렇다.
   * 이 동안 컴포넌트는 "액티비티를 고르세요"·"데이터 없음" 대신 로딩을 보여 준다 (2026-10-02 요청).
   * 목록이 비어 있으면(그 기간에 분석된 액티비티가 없으면) 기다릴 것이 없으므로 바로 빈 상태로 간다
   */
  const isFilterResolving =
    isPeriodActivitiesLoading || (!isTrend && !filterActivityId && analyzedActivityIds.length > 0)

  const { data: trendParticipatedPlayers, isLoading: isTrendPlayerListLoading } =
    useActivityParticipatedPlayerListQuery(trendActivityIds, isTrend && trendActivityIds.length > 0)
  const trendPlayerIds = useMemo(() => {
    if (!isTrend) {
      return []
    }
    const selected = new Set(filterPlayerIds)

    return (trendParticipatedPlayers ?? [])
      .map((player) => player.idfPlayer)
      .filter((id) => selected.has(id))
  }, [isTrend, trendParticipatedPlayers, filterPlayerIds])

  const trendEnabled = isTrend && trendActivityIds.length > 0 && trendPlayerIds.length > 0
  const { data: trendPlayerSummary, isLoading: isTrendPlayersLoading } = useTrendPlayerSummaryQuery(
    { idfActivityList: trendActivityIds, idfPlayerList: trendPlayerIds },
    trendEnabled,
  )
  const { data: trendActivitySummary, isLoading: isTrendActivitiesLoading } =
    useTrendPlayerActivitySummaryQuery(
      { idfActivityList: trendActivityIds, idfPlayerList: trendPlayerIds },
      trendEnabled,
    )

  const { data: metricListData, isLoading: isMetricLoading } = useMetricList()
  const { data: unitListData, isLoading: isUnitLoading } = useUnitQuery()

  const unitList: Unit[] = useMemo(
    () => (Array.isArray(unitListData) ? unitListData : []),
    [unitListData],
  )

  const metricList: MetricMeta[] = useMemo(
    () => (Array.isArray(metricListData) ? metricListData : []),
    [metricListData],
  )

  const metricMap = useMemo(() => {
    const map = new Map<string, MetricMeta>()
    metricList.forEach((m) => map.set(m.key, m))

    return map
  }, [metricList])

  const filterPlayers = useMemo<ActivityParticipatedPlayerType[]>(() => {
    // 트렌드는 기간 안 액티비티들의 참가 선수가 후보. 액티비티 리포트와 같게 "선택 0명 = 데이터 없음"
    if (isTrend) {
      const players = (trendParticipatedPlayers ?? []) as ActivityParticipatedPlayerType[]
      const selectedIds = new Set(filterPlayerIds)

      return players.filter((p) => selectedIds.has(p.idfPlayer))
    }
    const players = activityDetail?.players ?? []
    if (filterPlayerIds.length === 0) {
      return []
    }
    const selected = new Set(filterPlayerIds)

    return players.filter((p) => selected.has(p.idfPlayer))
  }, [activityDetail, filterPlayerIds, isTrend, trendParticipatedPlayers])

  const resolveItemPlayerIds = useCallback(
    (itemPlayerIds?: number[]) => {
      const filterIds = filterPlayers.map((p) => p.idfPlayer)
      if (!itemPlayerIds) {
        return filterIds
      }
      // 사용자가 직접 전부 해제한 경우는 "전체"가 아니라 "없음"이다
      if (itemPlayerIds.length === 0) {
        return []
      }
      const allowed = new Set(filterIds)
      const narrowed = itemPlayerIds.filter((id) => allowed.has(id))

      return narrowed.length > 0 ? narrowed : filterIds
    },
    [filterPlayers],
  )

  // 트렌드는 기간 안에 분석된 액티비티가 있으면 "선택된" 상태 — 액티비티를 따로 고르지 않는다
  const hasActivitySelection = isTrend ? trendActivityIds.length > 0 : !!filterActivityId
  const hasFilterSelection = isTrend
    ? trendActivityIds.length > 0 && filterPlayers.length > 0
    : hasActivitySelection &&
      filterPlayers.length > 0 &&
      (reportMode === ACTIVITY_SESSION_TYPE.ACTIVITY || filterSessionIds.length > 0)

  const value = useMemo<PdfDataContextType>(
    () => ({
      activityDetail: activityDetail ?? null,
      playerSummary,
      reportMode,
      reportTemplate,
      reportTemplateId,
      filterPlayers,
      resolveItemPlayerIds,
      hasActivitySelection,
      isFilterResolving,
      hasFilterSelection,
      metricList,
      metricMap,
      unitList,
      isLoading: isTrend
        ? isPeriodActivitiesLoading ||
          isTrendPlayerListLoading ||
          isTrendPlayersLoading ||
          isTrendActivitiesLoading ||
          isTemplateLoading ||
          isMetricLoading ||
          isUnitLoading
        : isActivityLoading ||
          isSummaryLoading ||
          isTemplateLoading ||
          isMetricLoading ||
          isUnitLoading,
      teamInfo,
      reportKind,
      trendPlayers: (trendPlayerSummary ?? []) as TrendPlayerRow[],
      trendActivities: (trendActivitySummary ?? []) as TrendActivityRow[],
      trendActivityIds,
      trendPlayerCandidates: (trendParticipatedPlayers ?? []) as ActivityParticipatedPlayerType[],
    }),
    [
      activityDetail,
      playerSummary,
      reportMode,
      reportTemplate,
      reportTemplateId,
      filterPlayers,
      resolveItemPlayerIds,
      hasActivitySelection,
      isFilterResolving,
      hasFilterSelection,
      metricList,
      metricMap,
      unitList,
      isActivityLoading,
      isSummaryLoading,
      isTemplateLoading,
      isMetricLoading,
      isUnitLoading,
      teamInfo,
      reportKind,
      trendPlayerSummary,
      trendActivitySummary,
      trendActivityIds,
      trendParticipatedPlayers,
    ],
  )

  return <PdfDataContext.Provider value={value}>{children}</PdfDataContext.Provider>
}

export function usePdfDataContext() {
  return useContext(PdfDataContext)
}
